import React, { useEffect, useState } from 'react';
import { Stack, Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { MyButton, MyTypography } from '../../../common/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { gridSpacing } from '../../../../common/themes/constants';
import Swal from 'sweetalert2';
import BackendErrorHandler from '../../../common/components/hook-form/BackendErrorHandler';
import CreateProviderForm, {
  initialValues as createProviderInitialValues,
  createProviderValidationSchema,
} from '../CreateProvider/CreateProviderForm';
import { SAVE_CLINIC_PROVIDER_MUTATION, SAVE_CLINIC_USER_MUTATION } from '../../gql/Mutation';
import clientGraphql from '../../../../common/apollo-graphql';
import { useMutation } from '@apollo/client';
import { isEmpty } from 'lodash';

const validationSchema = createProviderValidationSchema;

export default function AddClinicProviderDialog({
  open,
  onClose,
  clinicId,
  label,
  isDefault = false,
  defaultValues,
}) {
  const [cachedUser, setCachedUser] = useState(null);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...(defaultValues || createProviderInitialValues),
    },
    resolver: yupResolver(validationSchema),
    shouldFocusError: true,
    reValidateMode: 'onChange',
  });

  const [save_clinic_user] = useMutation(SAVE_CLINIC_USER_MUTATION, {
    client: clientGraphql,
  });

  const handleSuccess = async (providerResponse) => {
    const providerId = providerResponse?.save_clinic_provider?.id;
    if (cachedUser && providerId) {
      await save_clinic_user({
        variables: {
          clinic_id: clinicId,
          user: {
            ...cachedUser,
            providerIndex: parseInt(providerId),
          },
        },
      });
    }

    Swal.fire({
      icon: 'success',
      text: 'Successfully created a provider',
      showConfirmButton: true,
    }).then(async () => {
      reset();
      onClose(true);
    });
  };

  const handleError = (err) => {
    const graphErr = !isEmpty(err.graphQLErrors) ? err.graphQLErrors[0] : {};
    if (
      !isEmpty(graphErr.extensions) &&
      !isEmpty(graphErr.extensions.validation) &&
      JSON.stringify(graphErr.extensions.validation).includes('provider')
    ) {
      Swal.fire({
        icon: 'error',
        text: JSON.stringify(graphErr.extensions.validation),
        showConfirmButton: true,
      });
    } else {
      Swal.fire({
        icon: 'error',
        text: 'Error Message: ' + err.message,
        showConfirmButton: true,
      });
    }
  };

  const [save_clinic_provider, { loading: saveClinicProviderLoading }] = useMutation(
    SAVE_CLINIC_PROVIDER_MUTATION,
    {
      client: clientGraphql,
      onCompleted: handleSuccess,
      onError: handleError,
    }
  );

  const onSubmit = async (data) => {
    const newData = { ...data };

    if (
      newData.optional_documents.length === 0 ||
      newData.optional_documents.filter(e => e.document === 'misc').length === 0
    ) {
      newData.optional_documents.push({ document: 'misc', question: '' });
    }

    try {
      await save_clinic_provider({
        client: clientGraphql,
        variables: { clinic_id: clinicId, provider: newData },
      });
    } catch (err) {
      console.error('Mutation error', err);
      Swal.fire({
        icon: 'error',
        text: 'Something went wrong while saving. Please try again.',
      });
    }
  };

  useEffect(() => {
    if (open) {
      setCachedUser(null);
    }
  }, [open]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BackendErrorHandler
        error={false} // handled by handleError
        setError={setError}
        clearErrors={clearErrors}
      />
      <Dialog open={open} onClose={onClose} disablePortal={true}>
        <DialogContent>
          <Grid container direction="column" spacing={gridSpacing}>
            <Grid item xs={10}>
              <MyTypography variant="h3">{label ?? 'Add Provider'}</MyTypography>
            </Grid>
            <Grid item xs={10}>
              <CreateProviderForm
                control={control}
                errors={errors}
                setValue={setValue}
                trigger={trigger}
                getValues={getValues}
                isDefault={isDefault}
                cacheUserData={setCachedUser}
                cachedUser={cachedUser}
                watch={watch}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <MyButton onClick={onClose} disabled={saveClinicProviderLoading} fullWidth={false}>
              Cancel
            </MyButton>
            <MyButton
              type="submit"
              variant="contained"
              loading={saveClinicProviderLoading}
              fullWidth={false}
            >
              Add Provider
            </MyButton>
          </Stack>
        </DialogActions>
      </Dialog>
    </form>
  );
}
